import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { conflictingSources, painPoints, searchQuestions, symptoms, systemShows } from '../data/painPoints';
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }
} as const;
export function PainPoints() {
  return <section className="py-24 px-4 bg-sage-50" data-id="element-154" data-mp="1xwydw6is1m14">
      <div className="max-w-4xl mx-auto" data-id="element-155" data-mp="21zcwqqmg4lse">
        <motion.div {...reveal} className="text-center mb-12" data-id="element-156" data-mp="220e70svn1492">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-forest uppercase" data-id="element-157" data-mp="1nztpe01gleh5">
            Still Trying to Piece Together Hydroponics From Random Information?
          </h2>
          <div className="text-lg text-charcoal leading-relaxed bg-white p-6 md:p-8 rounded-2xl border border-sage-200 text-left space-y-4" data-id="element-158" data-mp="3idl5nieamwlk">
            <p data-id="element-159" data-mp="3nbmmrxs5slue">
              {conflictingSources.map(line => <React.Fragment key={line} data-id="element-160" data-mp="1z0exyqe3uy49">
                  {line}
                  <br data-id="element-161" data-mp="1yyc0jlami2pi" />
                </React.Fragment>)}
            </p>
            <p className="text-forest font-semibold" data-id="element-162" data-mp="1u1bx7i55re23">
              {symptoms.map((line, index) => <React.Fragment key={line} data-id="element-163" data-mp="27v340iri8ju4" data-mpi={index}>
                  {line}
                  {index < symptoms.length - 1 && <br data-id="element-164" data-mp="23thun7e6inoo" />}
                </React.Fragment>)}
            </p>
            <p data-id="element-165" data-mp="1g22p75r3vq97">And suddenly you’re searching again:</p>
            <ul className="space-y-2 pl-1" data-id="element-166" data-mp="1b3hol57xz4k8">
              {searchQuestions.map(question => <li key={question} className="flex items-start gap-3 text-forest font-semibold" data-id="element-167" data-mp="1p2r5660280oq">
                  <span className="w-1.5 h-1.5 rounded-full bg-leaf shrink-0 mt-3" aria-hidden="true" data-id="element-168" data-mp="1k2wbjuowgwzz" />
                  <span data-id="element-169" data-mp="30guw13bcp8pk">{question}</span>
                </li>)}
            </ul>
            <p className="text-forest font-semibold" data-id="element-170" data-mp="2vj2xhqg7aoce">Hydroponics can be simple.</p>
            <p data-id="element-171" data-mp="1t5f1vzlmhb3r">
              But trying to build the entire process from disconnected videos, posts and advice can
              make it feel much harder than it needs to be.
            </p>
          </div>
        </motion.div>

        <motion.div {...reveal} data-id="element-172" data-mp="226wg1cfgz2pt">
          <h3 className="text-xl font-bold text-forest mb-8 text-center uppercase tracking-wide" data-id="element-173" data-mp="226mlrcne69eb">
            The Real Cost of Growing Without a System
          </h3>
          <div className="overflow-hidden rounded-xl border border-sage-200 bg-white shadow-sm shadow-forest/5" data-id="element-174" data-mp="1afovef8hz0w0">
            {painPoints.map((point, index) => <motion.p key={point} initial={{
            opacity: 0,
            x: -12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.25,
            delay: Math.min(index * 0.04, 0.3),
            ease: [0.23, 1, 0.32, 1]
          }} className="flex items-start gap-3 px-5 py-4 text-charcoal text-sm leading-relaxed border-t first:border-t-0 border-sage-200" data-id="element-175" data-mp="1jfbx725528pl" data-mpi={index}>
                <X size={16} className="text-forest/60 shrink-0 mt-0.5" aria-hidden="true" data-id="element-176" data-mp="o5gtekfjwo90" />
                {point}
              </motion.p>)}
          </div>

          <div className="text-center mt-12 pt-8 border-t border-sage-200" data-id="element-177" data-mp="e713gks9fl13">
            <p className="text-2xl font-bold tracking-tight text-forest" data-id="element-178" data-mp="a70bdxc8nbbk">
              You don’t need more random hydroponic information.
            </p>
            <p className="text-xl mt-4 text-charcoal" data-id="element-179" data-mp="58p5fxp2dkym">
              You need a <span className="text-leaf font-semibold" data-id="element-180" data-mp="fmg61rbkxo8">system</span> that shows you:
            </p>
            <ul className="mt-5 space-y-1.5" data-id="element-181" data-mp="3ro0bwv8fgfqm">
              {systemShows.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-leaf" data-id="element-182" data-mp="3mox6h9ojc7i4">
                  {line}
                </li>)}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>;
}